/**
 * Renders each scene's strip poster at its saved time. Debounced, and skipped
 * while an export is running so the two don't compete for decoders.
 */

import { useEffect, useState } from 'react';
import type { ProjectPage, VideoScene } from '@canvas/shared-types';
import { renderSceneThumbToDataUrl } from '@/lib/video/sceneThumb';
import { useVideoStore } from '@/stores/videoStore';

export function useScenePosters(
  pages: ProjectPage[],
  scenes: VideoScene[] | undefined
): Record<string, string> {
  const [posters, setPosters] = useState<Record<string, string>>({});
  const exporting = useVideoStore((s) => s.exportController !== null);

  useEffect(() => {
    if (!scenes?.length || exporting) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        const next: Record<string, string> = {};
        for (const scene of scenes) {
          if (cancelled) return;
          const page = pages.find((p) => p.pageId === scene.pageId);
          if (!page) continue;
          try {
            next[scene.pageId] = await renderSceneThumbToDataUrl({ page, scene });
          } catch (err) {
            console.warn('[sceneThumb] poster failed', err);
          }
        }
        if (!cancelled) setPosters(next);
      })();
    }, 700);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [pages, scenes, exporting]);

  return posters;
}
